import { type RefObject, useEffect } from "react"

interface Options {
	sentinelRef: RefObject<Element | null>
	enabled: boolean
	onIntersect: () => void
	/** Distance in px before the sentinel actually enters the viewport. Default 200. */
	rootMargin?: string
}

/**
 * Fires `onIntersect` once each time the sentinel element enters the
 * viewport, while `enabled` is true. Uses native IntersectionObserver
 * against the document viewport (root: null). Designed for cursor-paginated
 * infinite lists that scroll via the window — for inner-scroll-container +
 * settle-delay use cases (e.g. chat auto-ack), see `useBottomVisibleTrigger`.
 */
export function useInfiniteScrollSentinel({
	sentinelRef,
	enabled,
	onIntersect,
	rootMargin = "200px",
}: Options): void {
	useEffect(() => {
		if (!enabled) return
		const el = sentinelRef.current
		if (!el) return
		const io = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (entry.isIntersecting) onIntersect()
				}
			},
			{ root: null, rootMargin },
		)
		io.observe(el)
		return () => io.disconnect()
	}, [sentinelRef, enabled, onIntersect, rootMargin])
}
